import { Form, Formik, FormikProps } from 'formik'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash/fp'
import * as yup from 'yup'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'

import {
  regExpEmailFilter,
  identificationCodeFilter,
  identificationToDate,
  personNameFilter,
  phoneFilter,
  postCodeFilter,
  getAge,
  dateToISO,
} from '../../helpers'
import theme from '../../theme'
import { regExpEmailCheck, phoneForValidation } from '../../helpers'
import { CaseTypeType, ClaimantDataType, InsuranceDataType } from '../../models'
import { useStyles } from '../../pages/styled'
import TextField from '../TextField'
import { CountryPicker } from '../CountryPicker'
import { Ga4Form } from './Ga4Form'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { CheckBoxField } from '../CheckBoxField'
import { RadioButtonsField } from '../RadioButtonsField'
import { objectToLookup } from '../../helpers/miscFunctions'

export type ClaimantFormData = {
  claimantData?: ClaimantDataType
  insuranceData?: InsuranceDataType
}

const ClaimantForm = (params: {
  caseReportId: string
  claimantFormData: ClaimantFormData
  onPageContinue: (claimantData: ClaimantDataType) => void
  onPageBack: () => void
  isContinueInProgress: boolean
  errorMessage: string
  setIfEqual: (isChanged: boolean) => void
  caseType: CaseTypeType | null
}) => {
  const classes = useStyles(theme)
  const { t } = useTranslation()

  const handleFormSubmit = (values: any) => {
    const claimantDataParams: ClaimantDataType =
      transformFormikValueToClaimantData(values)

    params.onPageContinue(claimantDataParams)
  }

  const transformFormikValueToClaimantData = (
    values: any
  ): ClaimantDataType => {
    const claimantType =
      params.caseType === 'TI' ? 'PERSON' : values.claimantType
    const isCompany = claimantType === 'COMPANY'
    return {
      claimantType,
      isInsuredPerson: isCompany ? undefined : values.isInsuredPerson,
      nonResident: isCompany ? undefined : values.nonResident,
      person: isCompany
        ? undefined
        : {
          idnum: values.nonResident ? null : values.identification,
          firstName: values.firstName,
          lastName: values.lastName,
        },
      company: isCompany
        ? {
          companyName: values.companyName,
          nip: values.nip,
        }
        : undefined,
      phone: values.phone,
      email: values.email,
      address: {
        street: values.street,
        town: values.town,
        country: values.country,
        postCode: values.zipCode,
      },
    }
  }

  const transformInputToFormikValues = (values: ClaimantFormData) => {
    const { claimantData } = values
    const claimantType =
      params.caseType === 'TI'
        ? 'PERSON'
        : claimantData?.claimantType ??
          (claimantData?.company != null
            ? 'COMPANY'
            : 'PERSON')
    return {
      claimantType,
      isInsuredPerson: claimantData?.isInsuredPerson ?? false,
      nonResident: claimantData?.nonResident ?? false,
      identification: claimantData?.person?.idnum ?? '',
      firstName: claimantData?.person?.firstName ?? '',
      lastName: claimantData?.person?.lastName ?? '',
      companyName: claimantData?.company?.companyName ?? '',
      nip: claimantData?.company?.nip ?? '',
      phone: claimantData?.phone ?? '',
      email: claimantData?.email ?? '',
      street: claimantData?.address?.street ?? '',
      town: claimantData?.address?.town ?? '',
      country: claimantData?.address?.country ?? 'POL',
      zipCode: claimantData?.address?.postCode ?? '',
    }
  }

  const formikInitialValues = transformInputToFormikValues(
    params.claimantFormData
  )

  const ClaimantSchema = ({ birthDate }: { birthDate: string }) =>
    yup.object().shape({
      claimantType: yup.string().required(t('optionRequired')),
      identification: yup
        .string()
        .when(['claimantType', 'nonResident'], {
          is: (claimantType: string, nonResident: any) =>
            claimantType === 'PERSON' && nonResident === false,
          then: yup
            .string()
            .required(t('secondPg.identification.err1'))
            .min(11, t('secondPg.identification.err2'))
            .test(
              'isValid',
              t('secondPg.identification.err5'),
              // @ts-ignore
              (value) => {
                if (value) {
                  const dateOfBirth: Date | null = identificationToDate(value)
                  if (!dateOfBirth) {
                    return false
                  } else {
                    return true
                  }
                }
              }
            )
            .test(
              'checkif18year',
              t('secondPg.identification.err4'),
              // @ts-ignore
              (value) => {
                if (value) {
                  // convert the calculated month difference in date format
                  const dateOfBirth: Date | null = identificationToDate(value)
                  if (dateOfBirth) {
                    const ageDiff: number = getAge(dateOfBirth)
                    return ageDiff >= 18
                  }
                }
              }
            )
            .when('isInsuredPerson', {
              is: (isInsuredPerson: any) => isInsuredPerson,
              then: yup.string().test(
                'checkBirthDate',
                t('secondPg.identification.err3'),
                // @ts-ignore
                (value) => {
                  if (value) {
                    const dateOfBirth: Date | null = identificationToDate(value)
                    if (dateOfBirth) {
                      return isEqual(dateToISO(dateOfBirth), birthDate)
                    }
                  }
                }
              ),
            }),
          otherwise: yup.string().nullable(),
        }),
      firstName: yup
        .string()
        .when('claimantType', {
          is: 'PERSON',
          then: yup
            .string()
            .required(t('secondPg.firstName.required'))
            .min(2, t('lengthText')),
        }),
      lastName: yup
        .string()
        .when('claimantType', {
          is: 'PERSON',
          then: yup
            .string()
            .required(t('secondPg.lastName.required'))
            .min(2, t('lengthText')),
        }),
      companyName: yup
        .string()
        .when('claimantType', {
          is: 'COMPANY',
          then: yup
            .string()
            .required(t('secondPg.companyName.required'))
            .min(2, t('lengthText')),
        }),
      nip: yup
        .string()
        .when('claimantType', {
          is: 'COMPANY',
          then: yup.string().required(t('secondPg.nip.required')),
        }),
      email: yup
        .string()
        .matches(regExpEmailCheck, t('email.formatErr'))
        .required(t('email.required')),
      phone: yup
        .string()
        .matches(phoneForValidation, t('secondPg.phone.err1'))
        .required(t('secondPg.phone.required')),
      street: yup.string().required(t('secondPg.street.required')),
      zipCode: yup.string().required(t('secondPg.zipCode.required')),
      town: yup.string().required(t('secondPg.city.required')),
      country: yup.string().nullable().required(t('secondPg.country.required')),
    })

  const formikRef: React.Ref<FormikProps<any>> = useRef(null)
  return (
    <Ga4Form formName={'ClaimantForm'} >
      <Formik
        initialValues={formikInitialValues}
        validationSchema={ClaimantSchema({
          birthDate: params.claimantFormData?.insuranceData?.birthDate ?? '',
        })}
        onSubmit={handleFormSubmit}
        enableReinitialize
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          handleChange,
          touched,
          handleBlur,
          setFieldValue,
        }) => {
          const isChanged = isEqual(values, initialValues)
          params.setIfEqual(isChanged)
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={12}
                  xl={12}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridMainForm}
                >
                  <Grid
                    item
                    sm={9}
                    xl={9}
                    xs={12}
                    container
                    direction="row"
                    className={classes.gridWorkArea}
                  >
                    <Grid
                      item
                      sm={12}
                      xs={12}
                      xl={12}
                      container
                      className={classes.gridResize}
                    >
                      <Typography className={classes.text} variant="h3">
                        {t('secondPg.title')}
                      </Typography>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        {params.caseType !== 'TI' && (
                          <Grid item xl={12} xs={12} sm={12}>
                            <RadioButtonsField
                              aria-label="claimantType"
                              name="claimantType"
                              label={t('secondPg.claimantType.label')}
                              required
                              value={values.claimantType}
                              onChange={handleChange}
                              lookup={objectToLookup(
                                t('secondPg.claimantType.code', {
                                  returnObjects: true,
                                })
                              )}
                              error={
                                Boolean(errors.claimantType && touched.claimantType)
                              }
                              helperText={
                                errors.claimantType && touched.claimantType
                                  ? errors.claimantType
                                  : null
                              }
                            />
                          </Grid>
                        )}
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid item xl={12} xs={12} sm={12}>
                          {values.claimantType === 'PERSON' &&
                            params.caseType !== 'TI' && (
                            <CheckBoxField
                              name="isInsuredPerson"
                              value={values.isInsuredPerson}
                              label={t([
                                `secondPg.isInsuredPerson.${params?.caseType}`,
                                'secondPg.isInsuredPerson.default',
                              ])}
                              checked={values.isInsuredPerson}
                              onChange={(e, checked) => {
                                setFieldValue(
                                  'isInsuredPerson',
                                  !values.isInsuredPerson
                                )
                                if (checked) {
                                  setFieldValue(
                                    'firstName',
                                    params.claimantFormData?.insuranceData
                                      ?.firstName
                                  )
                                  setFieldValue(
                                    'lastName',
                                    params.claimantFormData?.insuranceData?.lastName
                                  )
                                }
                              }}
                            />
                            )}
                        </Grid>
                      </Grid>
                      {values.claimantType === 'PERSON' ? (
                        <>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            xl={12}
                            container
                            className={classes.gridItem}
                            spacing={2}
                          >
                            <Grid
                              item
                              xl={6}
                              xs={6}
                              sm={6}
                              className={classes.fieldSize}
                            >
                              <TextField
                                regex={personNameFilter}
                                label={t('firstPg.firstName.label')}
                                placeholder={t('firstPg.firstName.hint')}
                                error={!!(errors.firstName && touched.firstName)}
                                name="firstName"
                                onChange={handleChange}
                                value={values.firstName}
                                helperText={
                                  errors.firstName && touched.firstName
                                    ? errors.firstName
                                    : null
                                }
                                className={classes.fieldSize}
                                InputProps={{ required: false }}
                                inputProps={{
                                  maxLength: 59,
                                }}
                                onBlur={handleBlur}
                                required
                                disabled={values.isInsuredPerson ? true : false}
                              />
                            </Grid>
                            <Grid
                              item
                              xl={6}
                              xs={6}
                              sm={6}
                              className={classes.fieldSize}
                            >
                              <TextField
                                regex={personNameFilter}
                                label={t('firstPg.lastName.label')}
                                placeholder={t('firstPg.lastName.hint')}
                                error={!!(errors.lastName && touched.lastName)}
                                name="lastName"
                                value={values.lastName}
                                onChange={handleChange}
                                helperText={
                                  errors.lastName && touched.lastName
                                    ? errors.lastName
                                    : null
                                }
                                className={classes.fieldSize}
                                InputProps={{ required: false }}
                                inputProps={{
                                  maxLength: 60,
                                }}
                                onBlur={handleBlur}
                                required
                                disabled={values.isInsuredPerson ? true : false}
                              />
                            </Grid>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            xl={12}
                            container
                            className={classes.gridItem}
                            spacing={2}
                          >
                            <Grid
                              item
                              xl={6}
                              xs={6}
                              sm={6}
                              className={classes.fieldSize}
                            >
                              <TextField
                                regex={identificationCodeFilter}
                                name="identification"
                                value={
                                  values.nonResident ? '' : values.identification
                                }
                                error={
                                  !!(
                                    errors.identification &&
                                    touched.identification
                                  )
                                }
                                label={t('secondPg.identification.label')}
                                placeholder={
                                  values.nonResident
                                    ? ''
                                    : t('secondPg.identification.hint')
                                }
                                helperText={
                                  errors.identification &&
                                  touched.identification
                                    ? errors.identification
                                    : null
                                }
                                onChange={handleChange}
                                className={classes.fieldSize}
                                InputProps={{ required: false }}
                                inputProps={{ maxLength: 11 }}
                                onBlur={handleBlur}
                                required={!values.nonResident}
                                disabled={values.nonResident}
                              />
                            </Grid>
                            <Grid
                              item
                              xl={6}
                              xs={6}
                              sm={6}
                              className={classes.fieldSize}
                              style={{
                                marginTop: '21px',
                                paddingTop: '6px',
                              }}
                            >
                              <CheckBoxField
                                name="nonResident"
                                value={values.nonResident}
                                label={t([`secondPg.nonResident.label`])}
                                checked={values.nonResident}
                                onChange={() => {
                                  setFieldValue(
                                    'nonResident',
                                    !values.nonResident
                                  )
                                  if (values.nonResident) {
                                    setFieldValue('identification', '')
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                        </>
                      ) : (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          xl={12}
                          container
                          className={classes.gridItem}
                          spacing={2}
                        >
                          <Grid
                            item
                            xl={6}
                            xs={6}
                            sm={6}
                            className={classes.fieldSize}
                          >
                            <TextField
                              label={t('secondPg.companyName.label')}
                              placeholder={t('secondPg.companyName.hint')}
                              error={
                                !!(errors.companyName && touched.companyName)
                              }
                              name="companyName"
                              value={values.companyName}
                              onChange={handleChange}
                              helperText={
                                errors.companyName && touched.companyName
                                  ? errors.companyName
                                  : null
                              }
                              className={classes.fieldSize}
                              InputProps={{ required: false }}
                              inputProps={{ maxLength: 100 }}
                              onBlur={handleBlur}
                              required
                            />
                          </Grid>
                          <Grid
                            item
                            xl={6}
                            xs={6}
                            sm={6}
                            className={classes.fieldSize}
                          >
                            <TextField
                              regex={identificationCodeFilter}
                              label={t('secondPg.nip.label')}
                              placeholder={t('secondPg.nip.hint')}
                              error={!!(errors.nip && touched.nip)}
                              name="nip"
                              value={values.nip}
                              onChange={handleChange}
                              helperText={
                                errors.nip && touched.nip ? errors.nip : null
                              }
                              className={classes.fieldSize}
                              InputProps={{ required: false }}
                              inputProps={{ maxLength: 20 }}
                              onBlur={handleBlur}
                              required
                            />
                          </Grid>
                        </Grid>
                      )}
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          sm={6}
                          xl={6}
                          xs={6}
                          className={classes.fieldSize}
                        >
                          <TextField
                            regex={regExpEmailFilter}
                            error={Boolean(errors.email && touched.email)}
                            id="email"
                            label={t('secondPg.email.label')}
                            placeholder={t('secondPg.email.hint')}
                            name="email"
                            value={values.email}
                            autoComplete="email"
                            onChange={handleChange}
                            helperText={
                              errors.email && touched.email ? errors.email : null
                            }
                            InputProps={{ required: false }}
                            inputProps={{ maxLength: 128 }}
                            onBlur={handleBlur}
                            required
                            className={classes.fieldSize}
                          />
                        </Grid>
                        <Grid
                          item
                          sm={6}
                          xl={6}
                          xs={6}
                          className={classes.fieldSize}
                        >
                          <TextField
                            regex={phoneFilter}
                            error={Boolean(errors.phone && touched.phone)}
                            id="phone"
                            label={t('secondPg.phone.label')}
                            placeholder={t('secondPg.phone.hint')}
                            name="phone"
                            onChange={handleChange}
                            helperText={
                              errors.phone && touched.phone ? errors.phone : null
                            }
                            value={values.phone}
                            InputProps={{ required: false }}
                            inputProps={{ maxLength: 15 }}
                            onBlur={handleBlur}
                            required
                            className={classes.fieldSize}
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          sm={12}
                          xl={12}
                          xs={12}
                          className={classes.fieldSize}
                        >
                          <TextField
                            label={t('secondPg.street.label')}
                            placeholder={t('secondPg.street.hint')}
                            error={!!(errors.street && touched.street)}
                            name="street"
                            value={values.street}
                            onChange={handleChange}
                            helperText={
                              errors.street && touched.street ? errors.street : null
                            }
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                            inputProps={{ maxLength: 60 }}
                            onBlur={handleBlur}
                            required
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          xl={6}
                          xs={6}
                          sm={6}
                          className={classes.fieldSize}
                        >
                          <TextField
                            regex={postCodeFilter}
                            label={t('secondPg.zipCode.label')}
                            placeholder={t('secondPg.zipCode.hint')}
                            error={!!(errors.zipCode && touched.zipCode)}
                            name="zipCode"
                            onChange={handleChange}
                            value={values.zipCode}
                            helperText={
                              errors.zipCode && touched.zipCode
                                ? errors.zipCode
                                : null
                            }
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                            inputProps={{ maxLength: 8 }}
                            onBlur={handleBlur}
                            required
                          />
                        </Grid>
                        <Grid
                          item
                          xl={6}
                          xs={6}
                          sm={6}
                          className={classes.fieldSize}
                        >
                          <TextField
                            label={t('secondPg.city.label')}
                            placeholder={t('secondPg.city.hint')}
                            error={!!(errors.town && touched.town)}
                            name="town"
                            value={values.town}
                            onChange={handleChange}
                            helperText={
                              errors.town && touched.town ? errors.town : null
                            }
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                            inputProps={{ maxLength: 60 }}
                            onBlur={handleBlur}
                            required
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          sm={12}
                          xl={12}
                          xs={12}
                          className={classes.fieldSize}
                        >
                          <CountryPicker
                            name="country"
                            label={t('secondPg.country.label')}
                            selectedItemCode={values.country ?? null}
                            onValueChanged={(
                              selectedCountryCode: string | null
                            ) => {
                              setFieldValue('country', selectedCountryCode)
                            }}
                            helperText={
                              errors.country && touched.country
                                ? errors.country
                                : null
                            }
                            error={Boolean(errors.country && touched.country)}
                            required={true}
                            onBlur={handleBlur}
                          />
                        </Grid>
                      </Grid>
                      <ErrorSubmitBack
                        name={'ClaimantForm'}
                        formik={formikRef.current}
                        pending={
                          params.isContinueInProgress
                        }
                        backLinkText={t('secondPg.backBtn')}
                        onBackLinkClick={params.onPageBack}
                      >
                        {t('continueBtn')}
                      </ErrorSubmitBack>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default ClaimantForm
